<template>
  <div
    :id="facet.filterOptionId"
    class="boost-facet"
    js-boost-facet="facet"
  >
    <button
      class="boost-facet__control"
      type="button"
      @click="toggleActiveControl"
    >
      <span v-text="facet.label" />
      <icon-arrow-down-filled />
    </button>

    <div class="boost-facet__content">
      <ul
        v-for="(chunk, chunkIndex) of facetChunks"
        :key="chunkIndex"
        class="boost-facet__content-inner"
        :class="[colorGridClass]"
      >
        <template
          v-for="(value, index) of chunk"
          :key="index"
        >
          <li>
            <boost-facet-value
              :id="valueKey(value)"
              :facet="facet"
              js-boost-facet="value"
              selector="value"
              :value="value"
              @click="toggleActiveValue"
            />
          </li>
          <ul v-if="value.subTags && value.subTags.length">
            <li
              v-for="(subValue, subValueIndex) of value.subTags"
              :key="subValueIndex"
            >
              <boost-facet-value
                :id="valueKey(subValue)"
                :facet="facet"
                js-boost-facet="value"
                selector="value"
                :value="subValue"
                @click="toggleActiveValue"
              />
            </li>
          </ul>
        </template>
      </ul>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Boost facet (main-collection)
 * -----------------------------------------------------------------------------
 * Facets for use with Boost filters.
 *
 * @param {String} choices - Available choices.
 * @param {Object} facet - Component facet.
 *
 */
import BoostFacetValue from '~async/boost-facet-value/boost-facet-value'

import IconArrowDownFilled from '~icons/direction/arrow/down-filled.svg'

export default {
  name: 'BoostFacet',

  components: {
    BoostFacetValue,
    IconArrowDownFilled,
  },

  props: {
    choices: {
      type: String,
      default: '',
    },
    facet: {
      type: Object,
      default: null,
    },
  },

  emits: {
    'choices-updated': (payload) => typeof payload === 'object',
    'facets-ready': () => true,
  },

  data() {
    return {
      sizeOrder: ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL', 'XS/S', 'M/L', 'OS'],
    }
  },

  computed: {

    facetChunks() {
      const chunks = []

      const chunkSize = 5
      for (let index = 0; index < this.values.length; index += chunkSize) {
        const chunk = this.values.slice(index, index + chunkSize)
        chunks.push(chunk)
      }

      return chunks
    },

    /**
     * Compute whether or not to include 'color grid' class for color facets.
     * @returns {String}
     */
    colorGridClass() {
      if (this.facet.filterOptionId === 'pf_opt_color') {
        return 'color-grid'
      }

      return ''
    },

    /**
     * Order facet values alphabetically, or in correct size order.
     * @returns {Array}
     */
    values() {
      const toSort = this.facet

      if (toSort.filterType === 'opt_size') {
        const sortedByNumber = toSort.values.sort((a, b) => this.valueKey(a) - this.valueKey(b))

        return sortedByNumber.sort((a, b) => {
          return this.sizeOrder.indexOf(this.valueKey(a)) - this.sizeOrder.indexOf(this.valueKey(b))
        })
      }

      return toSort.values.sort((a, b) => {
        const aKey = this.valueKey(a)
        const bKey = this.valueKey(b)

        if (aKey > bKey) {
          return 1
        }

        if (bKey > aKey) {
          return -1
        }

        return 0
      })
    },
  },

  watch: {

    /**
     * Watch choices change to emit event.
     */
    choices() {
      this.$emit('facets-ready')
    },

    /**
     * Watch facet change to emit event.
     */
    facet() {
      this.$emit('facets-ready')
    },
  },

  mounted() {
    this.$emit('facets-ready')
  },

  methods: {

    /**
     * Get key for value.
     * @param {Object} value
     * @returns {String}
     */
    valueKey(value) {
      return value.tag ? value.tag : value.key
    },

    /**
     * Toggle active class on facet click.
     * @param {Object} event - Click event.
     */
    toggleActiveControl(event) {
      const controls = document.querySelectorAll('[js-boost-facet="facet"]')
      const sortBy = document.querySelector('[js-boost-filter="sortBy"]')

      controls.forEach((control) => {
        const values = control.querySelectorAll('[js-boost-facet="value"]')

        if (control === event.currentTarget.parentNode) {
          if (control.classList.contains(this.$class('active'))) {
            control.classList.remove(this.$class('active'))
            values.forEach((value) => value.setAttribute('tabindex', -1))

          } else {
            control.classList.add(this.$class('active'))
            values.forEach((value) => value.setAttribute('tabindex', 0))
          }

        } else {
          control.classList.remove(this.$class('active'))
          values.forEach((value) => value.setAttribute('tabindex', -1))
        }
      })

      if (!sortBy) {
        return
      }

      const options = sortBy.querySelectorAll('[js-boost-filter="sortByOption"]')

      sortBy.classList.remove(this.$class('active'))
      options.forEach((option) => option.setAttribute('tabindex', -1))
    },

    /**
     * Format option for request for 'pf' filter types
     * @param {String} option - Filter option id.
     * @returns {String}
     */
    formatFilterOption(option) {
      let filterOptionId = option

      if (option.includes('pf_')) {
        filterOptionId = `${option}%5B%5D`
      }

      return filterOptionId
    },

    /**
     * Toggle active class on checkbox click.
     * @param {Event} event - Click event.
     */
    toggleActiveValue(event) {
      const valueElement = event.currentTarget
      const filterOptionId = this.formatFilterOption(valueElement.dataset.filterId)
      const encoded = encodeURI(valueElement.id)
      const requestParam = `&${filterOptionId}=${encoded}`

      let data = {
        action: 'add',
        requestParam,
      }

      if (valueElement.classList.contains(this.$class('active'))) {
        valueElement.classList.remove(this.$class('active'))

        data = {
          action: 'remove',
          requestParam,
        }

      } else {
        valueElement.classList.add(this.$class('active'))
      }

      this.$emit('choices-updated', data)
    },
  },
}
</script>

<style lang="scss">
@import './boost-facet';
</style>
